import { useState } from 'react'
import { Users, Trash2, Plus, Loader } from 'lucide-react'
import ReactECharts from 'echarts-for-react'

interface SerpCompetitor {
  id: number
  domain: string
  isManual: boolean
  occurrences: number
}

interface CompetitorsSectionProps {
  competitors: SerpCompetitor[]
  onAdd: (domain: string) => Promise<void>
  onDelete: (id: number) => Promise<void>
}

function domainColor(domain: string): string {
  let hash = 0
  for (let i = 0; i < domain.length; i++) {
    hash = domain.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 55%, 50%)`
}

const CHART_COLORS = [
  '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16',
]

export default function CompetitorsSection({ competitors, onAdd, onDelete }: CompetitorsSectionProps) {
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const maxOccurrences = Math.max(1, ...competitors.map(c => c.occurrences))
  const totalOccurrences = competitors.reduce((s, c) => s + c.occurrences, 0)

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = newDomain.trim().toLowerCase()
    if (!trimmed) return
    setAdding(true)
    try {
      await onAdd(trimmed)
      setNewDomain('')
    } finally {
      setAdding(false)
    }
  }

  async function handleDelete(id: number) {
    setDeletingId(id)
    try {
      await onDelete(id)
    } finally {
      setDeletingId(null)
    }
  }

  const chartOption = competitors.length > 0 ? {
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: '#1B2559',
      borderColor: '#1B2559',
      borderRadius: 8,
      padding: [8, 12],
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: (params: any) => {
        const pct = totalOccurrences > 0 ? Math.round((params.value / totalOccurrences) * 100) : 0
        return `<b>${params.name}</b><br/>${params.value} apparitions (${pct}%)`
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['45%', '75%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: '#fff', borderWidth: 2, borderRadius: 4 },
        label: { show: false },
        emphasis: {
          label: { show: true, fontSize: 11, fontWeight: 'bold' as const },
          itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.15)' },
        },
        data: competitors.slice(0, 10).map((c, i) => ({
          name: c.domain,
          value: c.occurrences,
          itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
        })),
      },
    ],
  } : null

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-orion-primary/10">
            <Users size={16} className="text-orion-primary" strokeWidth={1.25} />
          </div>
          <h3 className="text-sm font-semibold text-[#1B2559]">Concurrents</h3>
          {competitors.length > 0 && (
            <span className="text-xs text-[#8B8FA7] tabular-nums">{competitors.length}</span>
          )}
        </div>
      </div>

      {competitors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#F4F5FA] mb-2.5">
            <Users size={18} className="text-[#D1D5DB]" strokeWidth={1.25} />
          </div>
          <p className="text-sm text-[#8B8FA7]">Aucun concurrent detecte</p>
          <p className="text-xs text-[#B0B4C5] mt-0.5">Ajoutez-en ou lancez une analyse</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
            {competitors.map((comp, idx) => {
              const barWidth = (comp.occurrences / maxOccurrences) * 100
              const color = CHART_COLORS[idx % CHART_COLORS.length]
              return (
                <div
                  key={comp.id}
                  className="flex items-center gap-2.5 group py-2 px-2.5 -mx-2.5 rounded-xl hover:bg-[#F8F9FC] transition-colors"
                >
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ backgroundColor: color }}
                  >
                    {comp.domain.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-xs text-[#1B2559] font-medium min-w-[100px] truncate max-w-[140px]">
                    {comp.domain}
                  </span>
                  {comp.isManual && (
                    <span className="flex-shrink-0 text-[9px] font-semibold text-orion-primary bg-orion-primary/10 px-1.5 py-0.5 rounded-md uppercase tracking-wide">
                      Manuel
                    </span>
                  )}
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="flex-1 h-2 rounded-full bg-[#F4F5FA] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barWidth}%`, backgroundColor: color, opacity: 0.6 }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-[#555A73] flex-shrink-0 w-6 text-right tabular-nums">
                      {comp.occurrences}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDelete(comp.id)}
                    disabled={deletingId === comp.id}
                    className="flex-shrink-0 p-1 rounded-md text-[#D1D5DB] hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                    title="Supprimer"
                  >
                    {deletingId === comp.id ? (
                      <Loader size={13} strokeWidth={1.25} className="animate-spin" />
                    ) : (
                      <Trash2 size={13} strokeWidth={1.25} />
                    )}
                  </button>
                </div>
              )
            })}
          </div>

          {chartOption && (
            <div className="flex items-center justify-center">
              <ReactECharts option={chartOption} style={{ height: 220, width: '100%' }} />
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleAdd} className="flex items-center gap-2 mt-4 pt-4 border-t border-[#E2E4ED]">
        <input
          type="text"
          value={newDomain}
          onChange={e => setNewDomain(e.target.value)}
          placeholder="concurrent.fr"
          className="flex-1 text-sm px-3 py-2 rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:border-orion-primary focus:ring-1 focus:ring-orion-primary/20 transition-all"
        />
        <button
          type="submit"
          disabled={adding || !newDomain.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-orion-primary text-white text-sm font-medium rounded-xl hover:bg-orion-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          {adding ? (
            <Loader size={13} strokeWidth={1.25} className="animate-spin" />
          ) : (
            <Plus size={13} strokeWidth={1.5} />
          )}
          Ajouter
        </button>
      </form>
    </div>
  )
}
